import { ArrowsAltOutlined } from '@ant-design/icons';
import { WaterbodyDetail } from '@omniafishing/core';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import { useDimensions } from '../../hooks/use_dimensions';
import { useUser } from '../../hooks/use_user';
import { fitBounds } from '../../lib/fit_bounds';
import { formatToIso8601 } from '../../lib/time';
import { OmniaUrls } from '../../lib/urls';
import { WebAnalytics } from '../../lib/web_analytics';
import { RoutePaths } from '../../routes';
import {
  BASE_LAYER,
  CORE_LAYER,
  LAYER_TYPES,
  MapLayerState,
  WEATHER_LAYER,
} from '../map/map_types';
import { MapDefault } from '../map_default/map_default';
import { OmniaLinkButton } from '../omnia_button/omnia_link_button';
import SvgOmniaProLogo from '../svg/omnia_pro_logo';
import styles from './current_conditions_map.less';

interface CurrentConditionsMapProps {
  waterbodyDetail: WaterbodyDetail;
}

export const CurrentConditionsMap = (props: CurrentConditionsMapProps) => {
  const { waterbodyDetail } = props;
  const { primary_name, bbox, lat, lng, water_temperature, water_temperature_recorded_at } =
    waterbodyDetail;
  const [measureRef, bounds] = useDimensions();
  const { isPro } = useUser();

  const [maxBounds, setMaxBounds] =
    useState<[{ lat: number; lng: number }, { lat: number; lng: number }]>();

  // the lake lat lng is not always the midpoint of the bbox, midpoint works better for restricting the size
  const bboxMidPoint =
    bbox != null
      ? {
          lng: (bbox[0][0] + bbox[2][0]) / 2,
          lat: (bbox[0][1] + bbox[2][1]) / 2,
        }
      : {
          lng,
          lat,
        };

  const zoom =
    bounds.width && bbox
      ? fitBounds([bbox[0], bbox[2]], {
          width: bounds.width,
          height: bounds.height,
        }).zoom
      : 13;

  const mapLayerState: Partial<MapLayerState> = {
    [LAYER_TYPES.CORE]: CORE_LAYER.OMNIA_WATER_TEMP,
    [LAYER_TYPES.WEATHER]: [WEATHER_LAYER.WIND],
    [LAYER_TYPES.BASE]: BASE_LAYER.SATELLITE,
  };

  return (
    <>
      <div className={styles.chip}>
        <div className={styles.logo}>
          <SvgOmniaProLogo />
        </div>
        <div className={styles.chipContent}>
          <strong>Water Temp</strong>
          <div className={styles.averageWaterTemp}>
            <span className={styles.chipName}>{primary_name}</span>
            <div className={styles.waterTemp}>
              <p>{water_temperature}° F</p>
            </div>
          </div>
        </div>
        <div className={styles.chipFooter}>
          <span>
            Last Updated:{' '}
            <time dateTime={formatToIso8601(water_temperature_recorded_at)}>
              {dayjs(water_temperature_recorded_at).format('MMM D, YYYY')}
            </time>
          </span>
        </div>
      </div>
      <div className={styles.mapAndButtonWrapper}>
        <div className={styles.mapContainer} ref={measureRef}>
          {bounds.width > 0 && (
            <>
              <MapDefault
                disablePinFetch={true}
                onLoad={(mapBounds) => {
                  // lock the map to the bounds of the lake
                  setMaxBounds([
                    {
                      lat: mapBounds.s,
                      lng: mapBounds.w,
                    },
                    {
                      lat: mapBounds.n,
                      lng: mapBounds.e,
                    },
                  ]);
                }}
                mapLayerStateOverrides={mapLayerState}
                latitude={bboxMidPoint.lat}
                longitude={bboxMidPoint.lng}
                zoom={zoom}
                maxBounds={maxBounds}
                hideGeocoder
                navControl={false}
                className={styles.map}
                restrictAllMovement
              />
            </>
          )}
          {isPro ? (
            <OmniaLinkButton
              to={OmniaUrls.map({
                lat,
                lng,
                zoom: zoom - 2,
                waterbodySlug: waterbodyDetail.url_slug,
              })}
              kind="primary"
              size="md"
              className={styles.exploreMapButton}
            >
              <ArrowsAltOutlined />
              Explore Full Map
            </OmniaLinkButton>
          ) : (
            <OmniaLinkButton
              to={RoutePaths.PREMIUM_PRO}
              kind="primary"
              size="md"
              className={styles.exploreMapButton}
              onClick={() => {
                WebAnalytics.proButtonClicked({
                  location: 'current_conditions_map',
                  type: 'learn_more',
                  style: 'button',
                });
              }}
            >
              Explore Omnia PRO
            </OmniaLinkButton>
          )}
        </div>
      </div>
    </>
  );
};
